import React, { ChangeEvent, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import _ from 'lodash';
import cx from 'classnames';
import { nanoid } from "nanoid";

import { useDndMonitor } from "@dnd-kit/core";
import { rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";

import { AssetsLayoutEnum, MenuState, SectionBarProps, SectionState } from "../../types";

import { AutoGrowInput, Input } from "src/components/input";
import Reveal from "src/components/animation";
import Slider from "src/components/slider";
import Button from "src/components/button";

import { ScrollableContainer } from "src/components/scroller";
import { AssetCatergory, DealRoomAsset, DealRoomEditState } from "src/types/dealRoom";

import styles from './sections.module.scss';
import { DealRoomContext } from "../context";
import { useSpring, animated } from "@react-spring/web";
import { DraggableAssetsLayout } from "../assetsLayout";
import { useDispatch } from "react-redux";
import { addAssetToDealRoom, createSection, fetchDealRoomAssetsForSection, removeSection, renameSection } from "src/redux/actions/dealRoom";


const SectionBar: React.FC<SectionBarProps> = ({
  dealRoom, sections, selected, onSelectionChange, onMove, overSection
}) => {

  const idRef = useRef(nanoid(6));
  const { menuState, onMenuChange } = useContext(DealRoomContext);
  const navigate = useNavigate();
  const editing = menuState.primaryAction === 'edit';
  // const editing = true;
  const tabRef = React.useRef<any>(null);
  const dispatch = useDispatch();


  useDndMonitor({
    onDragOver(event) {
      const { active, over } = event;
      const sourceContainer = _.get(active, 'data.current.sortable.containerId');
      const section = _.get(active, 'data.current.section');
      const target = _.get(over, 'data.current.section');

      if (idRef.current === sourceContainer && section) {
        if (target && section.index !== target.index) {
          onMove(section.index, target.index);
        }
      }
    }
  });

  useEffect(() => {
    tabRef.current?.refresh();
  }, [editing]);

  const handleAddSection = () => {
    onMenuChange({
      ...menuState,
      primaryAction: 'edit',
      secondaryAction: 'add-section'
    });
  }

  const handleSelection = (section: SectionState) => {
    if (onSelectionChange) {
      onSelectionChange(section.index);
    }
  }

  const handleRemoveSection = (section: SectionState) => {
    const sectionIndex = section.index;

    if (dealRoom.id) {
      if (_.size(dealRoom.sections) === 1) {
        const section = _.get(dealRoom, "sections[0]", {});
        const assets = _.get(section, "item.assets.items", []);
        dispatch(addAssetToDealRoom({
          dealRoom: dealRoom.id,
          assets: assets.map(item => ({ id: item.id, asset: item.asset } as DealRoomAsset)),
          addAt: "end"
        }));
      }
      dispatch(removeSection({
        dealRoom: dealRoom.id!,
        index: sectionIndex,
      }));
    }

  }

  const handleSectionRename = (section: SectionState, value: string) => {
    dispatch(renameSection({
      dealRoom: dealRoom.id!,
      index: section.index,
      name: value
    }));
  }

  return (
    <SortableContext id={idRef.current} items={sections.map(section => section.id || 'new-section')} strategy={rectSortingStrategy}>
      <div className={cx(styles.content, { [styles.editing]: editing })}>
        <div className={styles.addSectionButtonContainer}>
          <Reveal show={editing}>
            <AddSectionButton dealRoom={dealRoom} onAdd={handleAddSection} />
          </Reveal>
        </div>
        <ScrollableContainer distance={300} leftArrowComponent={LeftArrow} rightArrowComponent={RightArrow}>
          <div className={styles.sectionsList}>
            {sections.map((section, index) => {
              return (
                <SectionItem section={section}
                  editing={editing}
                  key={section.id}
                  selected={selected === index}
                  overSection={overSection}
                  onClick={handleSelection}
                  onNameChange={handleSectionRename}
                  onRemove={handleRemoveSection}
                />
              )
            })}
          </div>
        </ScrollableContainer>
      </div>
    </SortableContext>
  );
}

const LeftArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.left)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const RightArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.right)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const AddSectionButton: React.FC<{ dealRoom: DealRoomEditState, onAdd: () => void }> = ({ dealRoom, onAdd }) => {
  const handleClick = () => {
    onAdd();
  }

  return (
    <>
      <div className={styles.addSectionButton} onClick={handleClick}>
        <i className={cx(styles.icon, "paperflite-add")}></i>
        <div className={cx(styles.name, styles.add)}>Add Section</div>
      </div>
    </>
  );
}


interface SectionItemProps {
  section: SectionState;
  overSection: string | null | undefined;
  editing: boolean;
  selected: boolean;
  // eslint-disable-next-line no-unused-vars
  onClick: (section: SectionState) => void;
  // eslint-disable-next-line no-unused-vars
  onRemove: (section: SectionState) => void;
  // eslint-disable-next-line no-unused-vars
  onNameChange: (section: SectionState, value: string) => void;
}

const SectionItem: React.FC<SectionItemProps> = ({ section, overSection, selected, editing, onClick, onNameChange, onRemove }) => {
  const [originalName, setOriginalName] = React.useState(section.name);

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({
    id: section.uid || nanoid(6),
    disabled: !editing,
    data: {
      section: section,
      type: 'section',
      accepts: ['section', 'asset'],
    }
  });

  useEffect(() => {
    setOriginalName(section.name);
  }, [editing]);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    onNameChange(section, e.target.value);
  }

  const handleRemove = () => {
    onRemove(section);
  }

  return (

    <div className={cx(styles.section, 'collection_section', {
      [styles.selected]: selected,
      [styles.editing]: editing,
      [styles.dragging]: isDragging,
      [styles.over]: overSection === section.id,
    })}
      ref={setNodeRef} {...listeners} {...attributes}
      data-section-index={section.index}
      onClick={() => onClick(section)}
    >
      <AutoGrowInput
        readOnly={!(editing && selected)}
        className={styles.input}
        placeholder={originalName}
        maxWidth={1000}
        value={section.name}
        onChange={handleNameChange}
      />

      {editing && selected &&
        <button className={styles.remove} onClick={handleRemove}>
          <i className="paperflite-close"></i>
        </button>
      }
    </div>
  );
}

const DraggingSectionCard: React.FC<{ section: SectionState }> = ({ section }) => {
  return (
    <p className={cx(styles.section, styles.dragPreview, 'collection_section')}>
      {section.name}
    </p>
  );
}

interface SectionContainerProps {
  dealRoom: DealRoomEditState;
  section: SectionState;

  // eslint-disable-next-line no-unused-vars
  onRemove: (section: SectionState, asset: DealRoomAsset) => void;

  // eslint-disable-next-line no-unused-vars
  onClick: (section: SectionState, asset: DealRoomAsset) => void;

  // eslint-disable-next-line no-unused-vars
  onMove: (section: SectionState, updatedAssets : DealRoomAsset[]) => void;

  // eslint-disable-next-line no-unused-vars
  onMoveToSection: (source: SectionState, target: number, asset: DealRoomAsset) => void;

  activeAssetLayout: AssetsLayoutEnum

}

const SectionContainer: React.FC<SectionContainerProps> = ({ dealRoom, activeAssetLayout, section, onClick, onRemove, onMove, onMoveToSection }) => {
  const [mounted, setMounted] = React.useState(true);
  const [hidden, setHidden] = React.useState(false);
  const dispatch = useDispatch();
  const [animation] = useSpring({
    height: section.active ? "auto" : 0,
    opacity: section.active ? 1 : 0,
    transform: section.active ? 'translate3d(0,0,0)' : 'translate3d(0, 100px, 0)',
    config: {
      duration: 200
    },
    onRest: () => {
      setHidden(!section.active);
    }
  }, [section]);

  useEffect(() => {
    if (section.active) {
      setMounted(section.active);
    }

    if (section.active) {
      setHidden(!section.active);
    }
  }, [section.active]);

  const handleAssetRemove = (asset: DealRoomAsset) => {
    onRemove(section, asset);
  }

  const handleAssetClick = (asset: DealRoomAsset) => {
    onClick(section, asset);
  }

  return (
    <animated.div style={animation} className={cx(styles.sectionContainer, {
      [styles.active]: section.active,
      [styles.hidden]: hidden
    })}>
      <div className={styles.inner}>
        {mounted &&
          <>
            <DraggableAssetsLayout
              dealRoom={dealRoom}
              assetsClassName={styles.assetsContainer}
              section={section.index}
              visible={section.active}
              assets={section.assets}
              fetchAssetsForPage={(page) => {
                if(section.loading) return;
                dispatch(fetchDealRoomAssetsForSection({
                  id: dealRoom.id ?? "",
                  page: page,
                  limit: 20,
                  assetCategory: AssetCatergory.INTERNAL,
                  sectionId: section.id ?? ""
                }))
              }}
              loading={section.assets.loading}
              activeAssetLayout={activeAssetLayout}
              onRemove={handleAssetRemove}
              onClick={handleAssetClick}
              onMove={(updatedAssets) => onMove(section, updatedAssets)}
              isRenderingSection={true}
              onMoveToSection={(target, asset) => onMoveToSection(section, target, asset)}
            />
          </>
        }
      </div>
    </animated.div>
  );
}

interface AddSectionDialogProps {
  dealRoomId: string;
  show: boolean;
  // eslint-disable-next-line no-unused-vars
  onFinish: (index: number) => void;
  onClose: () => void;
}

const AddSectionDialog: React.FC<AddSectionDialogProps> = ({ dealRoomId, show, onFinish, onClose }) => {

  const [name, setName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const dispatch = useDispatch();

  const { dealRoom } = useContext(DealRoomContext);

  useEffect(() => {
    if (show) {

      setName('');
      setError(null);
    }
  }, [show]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setError(null);
  }

  const handleCancel = () => {
    onClose();
  }

  const validateForm = () => {
    if (_.isEmpty(_.trim(name))) {
      return 'Please provide a valid name';
    }

    if (_.size(name) > 200) {
      return 'Folder name cannot be greater than 200 characters';
    }

    return null;
  }

  const handleConfirm = () => {
    const e = validateForm();
    if (e) {
      setError(e);
      return
    }

    if (dealRoomId) {
      dispatch(createSection({
        dealRoom: dealRoomId,
        name: name
      }));
    }

    onFinish(0);
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleConfirm();
  }

  return (
    <Slider.Dialog width="300" className={styles.addSectionDialog} show={show} onClose={onClose}>
      <Slider.Header
        title={<>creating <br />new section...</>}
      >
      </Slider.Header>
      <Slider.Body>
        <form className={styles.addSectionForm} onSubmit={handleSubmit}>
          <Input autoFocus className={styles.input} value={name} placeholder="Add a section name..." onChange={handleChange} />
          <Reveal show={!!error}>
            <div className={styles.error}>
              <i className="fa-solid fa-triangle-exclamation"></i>
              {error}
            </div>
          </Reveal>

          <div className={styles.helpText}>
            <p>
              Organizing your deal room in sections is most suited when you have more than ten pieces of content to share.
            </p>
            <p>
              Add multiple sections as your content grows to structure your deal room and help your audience get a birds eye view of your story board.
            </p>
          </div>
        </form>
      </Slider.Body>
      <Slider.Actions>
        <Button icon="paperflite-tick"
          text={'Create'}
          variant='primary'
          disabled={!name}
          onClick={handleConfirm}
        />
        <Button text="Cancel"
          variant='secondary'
          onClick={handleCancel}
        ></Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
}


export { SectionBar, DraggingSectionCard, SectionContainer, AddSectionDialog };